// Texts of the lines screens (list, form, leg editor, Flussonic state).
// Glossary: docs/design/i18n.md.

import { defineMessages, type MessageKey } from '@/shared/i18n'

export const messages = defineMessages({
  ru: {
    title: 'Линии',
    newLine: 'Новая линия',
    editLine: 'Правка линии',
    lineNamed: 'Линия «{name}»',
    hint:
      'Линия — постоянный адрес на приёмнике (вход Flussonic или стенограм) с одним или несколькими назначениями. Сигнал идёт по ней, пока какой-то канал подключён и в эфире; «подключено, не в эфире» — назначения ещё не соединены. Какой канал говорит на линию, выбирается на экране «Подачи» — линия сама источник не выбирает.',
    colLine: 'Линия',
    colSite: 'Площадка',
    colLegs: 'Назначения',
    colFlussonic: 'Flussonic',
    colBitrate: 'Битрейт',
    colBitrateSource: '(Flussonic)',
    colBitrateTitle: 'Из статистики Flussonic; агентский битрейт назначения показан на экране «Подачи»',
    colActions: 'Действия',
    disabled: 'выключена',
    streamIdTitle: 'задан ключ потока',
    passphraseTitle: 'задан пароль',
    rtmpUrlTitle: 'адрес публикации задан',
    exclusive: 'эксклюзивно',
    empty: 'Линий нет.',
    loading: 'Загрузка…',
    edit: 'Изменить',
    delete: 'Удалить',
    deleteTitle: 'Удалить линию?',
    deleteBody: 'Линия «{name}» будет удалена вместе с назначениями.',
    deleteChecking: 'Проверяю, что уйдёт вместе с ней…',
    deleteImpactFailed: 'Не удалось проверить, что уйдёт вместе с линией «{name}».',
    deleteWithRoutes: 'Вместе с ней подачи:',
    deleteRefusal:
      'Если подача в эфире, пульт откажет — сначала снимите её с эфира. Если подача занята в расписании, тоже откажет — сначала измените или удалите правило.',

    // Flussonic state of a line.
    flNotWatched: 'не наблюдается',
    flNoData: 'нет данных',
    flUnreachable: 'Flussonic недоступен',
    flForeign: 'чужой паблишер',
    flSlate: 'заставка',
    flAlive: 'поток есть',
    flNoInput: 'нет входа',
    legOptional: ', необязательная',
    legOptionalTag: 'необязательное',
    legMore: 'Дополнительно · при смене формата потока: {value}',
    legListen: 'SRT, ждём на порту {port}',

    // Line form.
    fieldSite: 'Площадка',
    fieldName: 'Название',
    fieldBrand: 'Бренд',
    enabled: 'Линия включена',
    flussonicWatch: 'Следить за потоком на Flussonic (заставка, чужой паблишер)',
    flussonicServer: 'Сервер',
    flussonicVia: 'Через',
    flussonicViaPreset: 'цель с API Flussonic',
    flussonicViaServer: 'сервер из настроек пульта',
    flussonicPreset: 'Цель',
    flussonicNoPresets: 'Нет целей с API Flussonic — добавьте доступы к API на странице «Цели».',
    flussonicPresetGone: 'цель без API или удалена',
    flussonicStreamDefault: 'Пусто — поток самой цели.',
    targetsLink: 'Цели',
    flussonicStream: 'Поток',
    flussonicExclusive: 'Эксклюзивно: чужой паблишер на этом потоке — аларм (иначе только бейдж)',
    addLeg: 'Добавить назначение',
    cancel: 'Отмена',
    saving: 'Сохраняем…',
    save: 'Сохранить',
    createLine: 'Создать линию',

    // Leg editor.
    legN: 'Назначение {n}',
    legSaved: ' · сохранена',
    legName: 'Имя',
    legRole: 'Роль',
    legProto: 'Протокол',
    legOnFormatChange: 'При смене формата потока',
    legRequired: 'Обязательное (без него подача не считается в эфире)',
    legMode: 'Кто подключается',
    legListenerForbidden: 'На этой площадке запрещено, чтобы приёмник подключался к нам.',
    legModeCaller: 'Мы подключаемся к приёмнику',
    legModeListener: 'Приёмник подключается к нам',
    legPaste: 'Вставьте ссылку целиком',
    legPasteHint: 'srt://хост:порт?streamid=…&passphrase=… или rtmp://хост/приложение/ключ — разберём по полям.',
    legPasteDone: 'Разобрано по полям: {fields}.',
    legPasteBad: 'Не похоже на ссылку srt:// или rtmp://хост/приложение/ключ. Поля можно заполнить вручную.',
    legHost: 'Хост',
    legPort: 'Порт',
    legTtl: 'TTL',
    legTtlHint: 'Пусто — по умолчанию агента (64 для юникаста, 16 для мультикаста). 1–255.',
    legInterface: 'Интерфейс',
    legInterfaceHint: 'Исходящий интерфейс мультикаст-группы (например eth1). Пусто — решает таблица маршрутов.',
    legDatagramHint: 'UDP и RTP не имеют рукопожатия: «подключена» значит, что сокет открыт и агент отправляет, а дошло ли до приёмника — неизвестно. Мультикаст-группа должна быть разрешена в egress.allow агента.',
    legLatency: 'Задержка, мс',
    legStreamId: 'Ключ потока (секрет)',
    legStreamIdHint: 'Выдаёт Flussonic: по нему он пускает поток.',
    legPassphrase: 'Пароль',
    legRtmpUrl: 'Адрес публикации RTMP (секрет)',
    legRtmpUrlHint: 'rtmp://host/app/ключ — ключ трансляции — последний сегмент адреса (после приложения), поэтому поле только на запись.',
    legBackupAddress: 'Адрес резервного сервера приёма',
    legBackupAddressHint: 'Адрес без ключа; ключ дописывается к нему последним сегментом при сохранении.',
    legBackupKeyHint: 'Ключ трансляции для резервного сервера: тот же, что у основного назначения, если площадка не выдала отдельный резервный. Хранится зашифрованным и не показывается.',
    removeLeg: 'Убрать назначение',
    legEgress: 'Профиль выхода',
    legViaProfile: 'через профиль выхода',
    legEgressHint: 'Через что назначение уходит наружу: напрямую или через туннель площадки. Упал туннель — назначение ждёт его и напрямую не уходит.',
    legEgressDirect: 'напрямую',
    legEgressPreset: 'по умолчанию цели: {name}',
    legEgressPresetDirect: 'по умолчанию цели: напрямую',
    legEgressMissing: 'у цели профиль по умолчанию «{name}», а на этой площадке профиля с таким именем нет — пульт откажет; выберите профиль сами',
    legEgressFbTunnel: 'Из РФ публикация на эту площадку заблокирована — нужен профиль вида «туннель».',
    legEgressGone: 'профиль удалён или недоступен',
    legKey: 'Ключ трансляции (секрет)',
    legKeyHint: 'Ключ из студии площадки. Хранится зашифрованным и не показывается.',
    legKeyKept: 'Ключ сохранён; введите новый, чтобы заменить.',
    legKeyPreset: 'У цели есть общий ключ: поле можно оставить пустым.',
    legAddress: 'Адрес трансляции',
    legAddressHint: 'rtmp(s)://host/app — без ключа: адрес у этой площадки свой на каждую трансляцию. Пусто — адрес цели ({url}).',
    legAddressHintNone: 'rtmp(s)://host/app — без ключа: адрес у этой площадки свой на каждую трансляцию.',
    legAddressKept: 'Сохранённый адрес остаётся, пока поле пустое.',

    // Leg roles and format-change behaviour.
    roleFlussonic: 'Flussonic',
    roleStenogram: 'stenogram',
    roleRelay: 'ретрансляция',
    roleOther: 'другое',
    onFormatSignal: 'сообщить потребителю (по умолчанию)',
    onFormatReconnect: 'переподключить назначение',
    onFormatHint:
      'Сообщить — поднять версию PMT и пометить разрыв, сессия не рвётся; так понимают Flussonic и stenogram. Переподключить — разорвать и поднять назначение заново: провал примерно 1–2 с, зато потребитель гарантированно перечитает параметры звука и видео. Переподключение нужно приёмникам, которые смену версии PMT игнорируют и после смены формата отдают мусор.',

    // Client-side checks.
    errListenerPassphrase: 'назначению-listener passphrase обязательна: к нему подключаются извне',
    errRequired: 'обязательное поле',
    errRtmpUrl: 'нужен адрес вида rtmp://host/app/ключ',
    errRtmpUrlKey: 'в адресе нет ключа: после хоста нужны два сегмента — приложение и ключ (rtmp://host/app/ключ); ключ — последний сегмент',
    errSite: 'выберите площадку',
    errBrandLen: 'не длиннее {max} символов',
    errNoLegs: 'нужно хотя бы одно назначение',
    errPortTaken: 'порт уже занят назначением {n}',
    errTtl: 'TTL: целое от 1 до 255 или пусто',
    errInterface: 'имя интерфейса: до 15 букв, цифр, «.», «_», «-»',
  },
  en: {
    title: 'Lines',
    newLine: 'New line',
    editLine: 'Edit line',
    lineNamed: 'Line “{name}”',
    hint:
      'A line is a permanent destination: a Flussonic input or a stenogram listener, with one or more destinations. The signal goes out on it while some route is on air — in the “armed” state the destinations are not connected yet. Which channel feeds the line right now is set on the “Routes” screen — the line does not pick its source itself.',
    colLine: 'Line',
    colSite: 'Site',
    colLegs: 'Destinations',
    colFlussonic: 'Flussonic',
    colBitrate: 'Bitrate',
    colBitrateSource: '(Flussonic)',
    colBitrateTitle: 'From Flussonic statistics; the agent’s own destination bitrate is on the “Routes” screen',
    colActions: 'Actions',
    disabled: 'off',
    streamIdTitle: 'streamid is set (secret)',
    passphraseTitle: 'passphrase is set',
    rtmpUrlTitle: 'publish URL is set',
    exclusive: 'exclusive',
    empty: 'No lines.',
    loading: 'Loading…',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete the line?',
    deleteBody: 'Line “{name}” will be deleted together with its destinations.',
    deleteChecking: 'Checking what goes with it…',
    deleteImpactFailed: 'Could not check what would go with line “{name}”.',
    deleteWithRoutes: 'Routes that go with it:',
    deleteRefusal:
      'If a route is on air, the console will refuse — take it off air first. If a route is used by the schedule, it will refuse too — change or delete the rule first.',

    flNotWatched: 'not watched',
    flNoData: 'no data',
    flUnreachable: 'Flussonic unreachable',
    flForeign: 'foreign publisher',
    flSlate: 'slate',
    flAlive: 'stream present',
    flNoInput: 'no input',
    legOptional: ', optional',
    legOptionalTag: 'optional',
    legMore: 'More · on a stream format change: {value}',
    legListen: 'SRT, listening on port {port}',

    fieldSite: 'Site',
    fieldName: 'Name',
    fieldBrand: 'Brand',
    enabled: 'Line enabled',
    flussonicWatch: 'Watch the stream on Flussonic (slate, foreign publisher)',
    flussonicServer: 'Server',
    flussonicVia: 'Through',
    flussonicViaPreset: 'a target with a Flussonic API',
    flussonicViaServer: 'a server from the console settings',
    flussonicPreset: 'Target',
    flussonicNoPresets: 'No target has a Flussonic API — add the API access on the “Targets” page.',
    flussonicPresetGone: 'a target without an API or deleted',
    flussonicStreamDefault: 'Empty — the target’s own stream.',
    targetsLink: 'Targets',
    flussonicStream: 'Stream',
    flussonicExclusive: 'Exclusive: a foreign publisher on this stream is an alarm (otherwise only a badge)',
    addLeg: 'Add destination',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    createLine: 'Create line',

    legN: 'Destination {n}',
    legSaved: ' · saved',
    legName: 'Name',
    legRole: 'Purpose',
    legProto: 'Protocol',
    legOnFormatChange: 'When the stream format changes',
    legRequired: 'Required (without it the route does not count as on air)',
    legMode: 'Who connects',
    legListenerForbidden: 'This site does not allow the receiver to connect to us.',
    legModeCaller: 'We connect to the receiver',
    legModeListener: 'The receiver connects to us',
    legPaste: 'Paste the whole link',
    legPasteHint: 'srt://host:port?streamid=…&passphrase=… or rtmp://host/app/key - it is split into the fields.',
    legPasteDone: 'Split into the fields: {fields}.',
    legPasteBad: 'This is not an srt:// or rtmp://host/app/key link. You can fill the fields by hand.',
    legHost: 'Host',
    legPort: 'Port',
    legTtl: 'TTL',
    legTtlHint: 'Empty - the agent default (64 unicast, 16 multicast). 1-255.',
    legInterface: 'Interface',
    legInterfaceHint: 'Outgoing interface of a multicast group (e.g. eth1). Empty - the routing table decides.',
    legDatagramHint: 'UDP and RTP have no handshake: "connected" means the socket is open and the agent is sending; whether it reaches the receiver is not known. The multicast group must be allowed in the agent egress.allow.',
    legLatency: 'Latency, ms',
    legStreamId: 'Stream key (secret)',
    legStreamIdHint: 'Issued by Flussonic: it lets the stream in by it.',
    legPassphrase: 'Password',
    legRtmpUrl: 'RTMP publish URL (secret)',
    legRtmpUrlHint: 'rtmp://host/app/key — the stream key is the last segment of the address (after the app), so the field is write-only.',
    legBackupAddress: 'Backup ingest address',
    legBackupAddressHint: 'The address without the key; the key is appended to it as the last segment on save.',
    legBackupKeyHint: 'The stream key for the backup ingest: the primary destination’s one unless the platform gave a separate backup key. Stored encrypted, never shown.',
    removeLeg: 'Remove destination',
    legEgress: 'Egress profile',
    legViaProfile: 'via an egress profile',
    legEgressHint: 'How the destination reaches the outside: direct or through a site tunnel. When the tunnel is down the destination waits for it and never goes direct.',
    legEgressDirect: 'direct',
    legEgressPreset: 'the target’s default: {name}',
    legEgressPresetDirect: 'the target’s default: direct',
    legEgressMissing: 'the target’s default profile is “{name}”, and this site has no profile of that name — the console will refuse; pick a profile yourself',
    legEgressFbTunnel: 'Publishing to this platform from Russia is blocked — a profile of kind “site tunnel” is needed.',
    legEgressGone: 'the profile is deleted or unavailable',
    legKey: 'Stream key (secret)',
    legKeyHint: 'The key from the platform’s studio. Stored encrypted, never shown.',
    legKeyKept: 'A key is stored; type a new one to replace it.',
    legKeyPreset: 'The target has a shared key: the field may stay empty.',
    legAddress: 'Broadcast address',
    legAddressHint: 'rtmp(s)://host/app — without the key: this platform has its own address for every broadcast. Empty = the target’s address ({url}).',
    legAddressHintNone: 'rtmp(s)://host/app — without the key: this platform has its own address for every broadcast.',
    legAddressKept: 'The stored address stays while the field is empty.',

    roleFlussonic: 'Flussonic',
    roleStenogram: 'stenogram',
    roleRelay: 'relay',
    roleOther: 'other',
    onFormatSignal: 'signal the consumer (default)',
    onFormatReconnect: 'reconnect the destination',
    onFormatHint:
      'Signal — bump the PMT version and mark a discontinuity, the session stays up; Flussonic and stenogram understand this. Reconnect — drop the destination and bring it up again: a gap of about 1–2 s, but the consumer is guaranteed to re-read the audio and video parameters. Reconnect is for receivers that ignore a PMT version change and output garbage after a format change.',

    errListenerPassphrase: 'a listener destination needs a passphrase: it is connected to from outside',
    errRequired: 'required field',
    errRtmpUrl: 'expected an address like rtmp://host/app/key',
    errRtmpUrlKey: 'the address has no key: after the host it needs two segments, the app and the key (rtmp://host/app/key); the key is the last segment',
    errSite: 'choose a site',
    errBrandLen: 'no longer than {max} characters',
    errNoLegs: 'at least one destination is needed',
    errPortTaken: 'port already used by destination {n}',
    errTtl: 'TTL: an integer from 1 to 255, or empty',
    errInterface: 'interface name: up to 15 letters, digits, ".", "_", "-"',
  },
})

/**
 * A read-only record whose values are looked up in `messages` on every read,
 * so a label table exported to other features follows the RU/EN switch.
 */
export function localRecord<K extends string>(keys: Readonly<Record<K, MessageKey<typeof messages.ru>>>): Readonly<Record<K, string>> {
  const out = {} as Record<K, string>
  for (const k of Object.keys(keys) as K[]) {
    Object.defineProperty(out, k, { enumerable: true, get: () => messages.t(keys[k]) })
  }
  return Object.freeze(out)
}
